import { UtilHelper } from "./util.helper"

export class CsvTransform {
    /**
     * Converts a CSV cell value to a trimmed string. Null, undefined, and empty string become "".
     */
    static trim(value: unknown): string {
        if (value === null || value === undefined || value === "") {
            return ""
        }

        return String(value).trim()
    }

    /**
     * Trims the value and returns undefined if the result is empty; otherwise returns the trimmed string. Used for optional CSV columns.
     */
    static toOptionalString(value: unknown): string | undefined {
        const s = CsvTransform.trim(value)

        return s === "" ? undefined : s
    }

    /**
     * Trims the value and parses it as a number. Returns undefined for empty,
     * whitespace-only, or non-numeric input. Strips common currency formatting.
     */
    static toOptionalNumber(value: unknown): number | undefined {
        const s = CsvTransform.trim(value)

        if (s === "") {
            return undefined
        }

        const normalized = s.replace(/[$,]/g, "")

        const num = Number(normalized)

        return Number.isFinite(num) ? num : undefined
    }

    /**
     * Parses a comma-separated CSV cell into an array of trimmed non-empty strings. Returns undefined for empty or whitespace-only input.
     */
    static toCommaSeparatedStrings(value: unknown): string[] | undefined {
        const s = CsvTransform.trim(value)

        if (s === "") {
            return undefined
        }

        const arr = s
            .split(",")
            .map(x => x.trim())
            .filter(Boolean)

        return arr.length ? arr : undefined
    }

    /**
     * Trims the value and attempts to parse it as a date using multiple possible formats.
     * If a valid date is found, returns it in MySQL date format (YYYY-MM-DD); otherwise returns the trimmed string.
     * Used for date fields that may be in various formats. Relies on UtilHelper.getPossibleDateField to try different date formats.
     */
    static dateToMysqlFormat(value: unknown): string {
        const result = CsvTransform.trim(value)

        const { valid: dateToUse } = UtilHelper.getPossibleDateField(result)

        if (!dateToUse) {
            return result
        }

        return UtilHelper.dateToMysqlFormat(dateToUse.toDate())
    }
}
